import Axios from "axios";
import { Post } from "../service/axiosService";
import * as localStorageService from "../service/localStorageService";
import { LocalStorageKeys } from "..//constants/localStorageKeys";
import Notifications from "../service/notification";
import i18n from "i18next";
import { Brand, isAdeed } from "../constants/brand";
let controller = new AbortController(); // Centralized AbortController
let interceptorsInitialized = false;

const setupAxiosInterceptors = (navigate) => {
    if (interceptorsInitialized) return; // Prevent duplicate setup
    interceptorsInitialized = true;
    const refreshToken = async () => {
        const refreshToken = localStorageService.getValue(LocalStorageKeys.RefreshToken);
        const accessToken = localStorageService.getValue(LocalStorageKeys.AuthToken);

        if (!refreshToken) {
            cancelAllRequests("Token refresh failed, logging out.");
            window.localStorage.clear();
            navigate("/login"); // Navigate to the login page
            return null;
        }

        try {
            const payload = { accessToken, refreshToken };
            const response = await Post(`${process.env.REACT_APP_COMMON_AUTH_API_URL}/Account/RefreshToken`, payload);

            if (response?.data?.data) {
                localStorageService.setKey(LocalStorageKeys.AuthToken, response.data.data.accessToken);
                localStorageService.setKey(LocalStorageKeys.RefreshToken, response.data.data.refreshToken);
                return response.data.data.accessToken;
            }
        } catch (error) {
            console.error("Failed to refresh token:", error);
            cancelAllRequests("Token refresh failed, logging out.");
            window.localStorage.clear();
            navigate("/login"); // Navigate to the login page
            return null;
        }
    };

    // Requests that get 401 together share one refresh call
    let refreshPromise = null;
    const refreshTokenOnce = () => {
        if (!refreshPromise) {
            refreshPromise = refreshToken().finally(() => {
                refreshPromise = null;
            });
        }
        return refreshPromise;
    };

  // Attach AbortController signal to each request
  Axios.interceptors.request.use((config) => {
    config.signal = controller.signal; // Attach the signal
    // The loadboard backend tells the apps apart by X-Organization; TruKKer sends no header
    if (isAdeed && isLoadboardApiUrl(config.url)) {
        config.headers["X-Organization"] = Brand.organization;
        config.url = toAdeedApiUrl(config.url);
    }
    return config;
});

    Axios.interceptors.response.use(
        (response) => response, // Pass successful responses
        async (error) => {

            if (error?.name === "CanceledError") {
                console.warn("Request canceled:", error.message);
                return Promise.reject(error);
            }

            if (error?.response?.status === 401) {
                const requestUrl = error.config?.url

                if(requestUrl?.indexOf("Account/RefreshToken")>-1){
                    return Promise.reject(error);
                }

                // Still 401 after a successful refresh (e.g. token from the other app, or the organization
                // switched off): log out instead of refreshing again
                if (error.config?._retry) {
                    cancelAllRequests("Request rejected after token refresh, logging out.");
                    window.localStorage.clear();
                    navigate("/login");
                    return Promise.reject(error);
                }

                const newAccessToken = await refreshTokenOnce();

                if (newAccessToken) {
                    error.config._retry = true;
                    error.config.headers["Authorization"] = `Bearer ${newAccessToken}`;
                    return Axios.request(error.config); // Retry the original request
                } else {
                    return Promise.reject(error);
                }
            }

            // Handle other errors
            const message =
                error?.response?.data?.message || error?.response?.data?.error ||
                error?.response?.data?.title ||
                i18n.t(error?.response?.data?.localizeKey, { defaultValue: "Please try again later!" });
            if(message !== "Pin not set. Verify with OTP to register your PIN." &&  message !== 'No matches found or inquiry number not present'){
                Notifications.error("error", `${message}!`);
            }

            return Promise.reject(error);
        }
    );
};

// Function to cancel all pending requests
export const cancelAllRequests = (reason = "Operation canceled by the user.") => {
    controller.abort(); // Cancel all ongoing requests
    console.log(reason); // Optional logging

    controller = new AbortController(); // Reset the controller for new requests
};

export default setupAxiosInterceptors;

// Loadboard backend bases (/api, /Auth/api). X-Organization goes only to these: the loadboard backend is the only service
// that reads it, and the gateways in front of master data (/mdm/api), Inquiry and InquiryJOB refuse it cross-origin.
// Each base keeps its trailing "/" so it can't match the whole host (the upload base is the bare host).
const LoadboardApiUrls = [
    process.env.REACT_APP_COMMON_API_URL,
    process.env.REACT_APP_COMMON_AUTH_API_URL,
]
    .map((url) => url?.trim().replace(/\/?$/, "/"))
    .filter((url) => url && url !== "/");

const isLoadboardApiUrl = (url) => !!url && LoadboardApiUrls.some((baseUrl) => url.startsWith(baseUrl));

// ADEED Freight calls the loadboard backend through its own host: the env's loadboard.<env>.trukker.com becomes
// adeedfreight.<env>.trukker.com (same backend, its own gateway routes). TruKKer keeps the env's host, and master
// data, Inquiry and InquiryJOB stay on theirs - they are not routed on the ADEED host.
const toAdeedApiUrl = (url) => url.replace(/^(https?:\/\/)loadboard\./i, "$1adeedfreight.");